import {
  type ReactNode, createContext, useContext, useEffect, useRef, useState,
} from 'react'
import { formatCount } from '../lib/format'

/** "Open all" and "Close all", as a request every section answers: an
 *  epoch that moves on each press, so pressing "Close all" twice still
 *  closes a section opened in between. */
const AllSections = createContext<{ epoch: number; open: boolean } | null>(null)

export function AdminSections({ children }: { children: ReactNode }) {
  const [all, setAll] = useState<{ epoch: number; open: boolean } | null>(null)
  const press = (open: boolean) => setAll((a) => ({ epoch: (a?.epoch ?? 0) + 1, open }))
  return (
    <AllSections.Provider value={all}>
      {/* One block, so a page's own spacing (the redesign's .page gap)
          applies around the sections and not between them. */}
      <div className="admin-sections">
        <div className="section-tools">
          <button type="button" className="link" onClick={() => press(true)}>Open all</button>
          <button type="button" className="link" onClick={() => press(false)}>Close all</button>
        </div>
        {children}
      </div>
    </AllSections.Provider>
  )
}

interface Props {
  /** The section's anchor, `#section-<id>`. */
  id: string
  title: string
  /** How much is inside: "31 stations". */
  count?: string
  /** Something waiting on an admin: "2 waiting for approval", shown on the
   *  header so a closed section still says it. */
  attention?: string
  /** One line saying what the section is, inside it. */
  note?: ReactNode
  /** Opens the section when it turns true: something elsewhere on the page
   *  has started work that lives in here (a form, say). */
  openWhen?: boolean
  children: ReactNode
}

/** One admin category, folded to its header until wanted. Every section
 *  starts closed on every visit (the owner's choice, 2026-10-13): the page
 *  opens as a list of categories, and the header says what is inside and
 *  whether anything is waiting, so a closed page still answers "is there
 *  anything for me here". Its contents mount on first opening and stay
 *  mounted while the page is open: a section that fetches does not fetch
 *  until someone looks, and a half-typed search survives closing it. */
export function AdminSection({ id, title, count, attention, note, openWhen,
                              children }: Props) {
  const [open, setOpen] = useState(false)
  const [seen, setSeen] = useState(false)
  const all = useContext(AllSections)
  const lastEpoch = useRef(all?.epoch ?? 0)

  useEffect(() => {
    if (!all || all.epoch === lastEpoch.current) return
    lastEpoch.current = all.epoch
    setOpen(all.open)
    if (all.open) setSeen(true)
  }, [all])

  useEffect(() => {
    if (!openWhen) return
    setOpen(true)
    setSeen(true)
  }, [openWhen])

  return (
    <details id={`section-${id}`} className="admin-section" open={open}
             onToggle={(event) => {
               const now = event.currentTarget.open
               if (now === open) return
               setOpen(now)
               if (now) setSeen(true)
             }}>
      <summary>
        <h2>{title}</h2>
        {count && <span className="section-count">{count}</span>}
        {attention && <span className="pill warn">{attention}</span>}
      </summary>
      {seen && (
        <div className="admin-section-body">
          {note && <p className="muted section-note">{note}</p>}
          {children}
        </div>
      )}
    </details>
  )
}

/** A list's search: the query, how many of the list it leaves, and a way
 *  back to all of them. */
export function ListSearch({ value, onChange, placeholder, shown, total, noun }: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  shown: number
  total: number
  noun: [string, string]
}) {
  const word = (n: number) => (n === 1 ? noun[0] : noun[1])
  return (
    <div className="list-search">
      <input type="search" className="search-input" value={value}
             aria-label={`Search ${noun[1]}`} placeholder={placeholder}
             autoComplete="off" spellCheck={false}
             onChange={(event) => onChange(event.target.value)}
             onKeyDown={(event) => {
               if (event.key === 'Escape' && value) {
                 event.preventDefault()
                 onChange('')
               }
             }} />
      <span className="muted list-count" role="status">
        {value.trim()
          ? `${formatCount(shown)} of ${formatCount(total)} ${word(total)}`
          : `${formatCount(total)} ${word(total)}`}
      </span>
    </div>
  )
}

/** The first `page` items, and a button for the next page: a list of
 *  thousands is searched, not scrolled, and drawing all of it at once is
 *  what made the Admin page slow to open. */
export function useShowMore<T>(items: T[], page: number, resetOn: unknown) {
  const [limit, setLimit] = useState(page)
  useEffect(() => { setLimit(page) }, [resetOn, page])
  const left = Math.max(items.length - limit, 0)
  const more = left > 0 ? (
    <div className="show-more">
      <button type="button" className="button" onClick={() => setLimit((l) => l + page)}>
        Show {Math.min(page, left)} more
      </button>
      <span className="muted">{formatCount(left)} not shown</span>
    </div>
  ) : null
  return { visible: items.slice(0, limit), more }
}
